import type { AppProps } from "next/app";
import { EmotionCache } from "@emotion/react";
import { FC, useEffect, useMemo } from "react";
import { ThemeProvider } from "styled-components";
import { Provider as ReduxProvider } from "react-redux";
import { IntlProvider } from "react-intl";
import { Page } from "../../types/Page";
import { ApolloProvider } from "../apollo";
import { LaunchDarklyProvider } from "../launchdarkly";
import { MaterialProvider } from "../material";
import { Base } from "../modules/base";
import { Reset } from "../styles/Reset";
import { Global } from "../styles/Global";
import { theme } from "../styles/theme";
import { store } from "../redux";
import { refreshSavedCredentials } from "../modules/auth/authSlice";
import { Tokens } from "../styles/Tokens";
import { useShortLocale } from "../hooks/useShortLocale";
import "../../styles/globals.css";

import English from "../../content/compiled-locales/en.json";
import Hebrew from "../../content/compiled-locales/he.json";

const PunctClient: FC<
  AppProps & {
    Component: Page;
    // Injected per-request by _document.tsx's renderPage enhancer so
    // MUI v5's Emotion styles can be extracted server-side (see
    // MUI_MIGRATION_PLAN.md Phase 3). Undefined on the client, where
    // MaterialProvider falls back to its own shared cache.
    emotionCache?: EmotionCache;
  }
> = ({ Component, pageProps, emotionCache }) => {
  const shortLocale = useShortLocale();

  useEffect(() => {
    // Runs strictly after the first commit (React's guarantee for
    // useEffect) - see store.ts's comment on why that guarantee matters
    // here specifically, unlike a module-scope dispatch.
    store.dispatch(refreshSavedCredentials());
  }, []);

  const messages = useMemo(() => {
    switch (shortLocale) {
      case "en":
        return English;
      case "he":
        return Hebrew;
      default:
        return Hebrew;
    }
  }, [shortLocale]);

  return (
    <>
      <Reset />
      <Global />
      <Tokens />

      <MaterialProvider emotionCache={emotionCache}>
        <ThemeProvider theme={theme}>
          <ReduxProvider store={store}>
            <ApolloProvider>
              <LaunchDarklyProvider>
                <IntlProvider
                  locale={shortLocale}
                  messages={messages}
                  onError={console.error}
                >
                  <Base fullScreen={Component.fullScreen}>
                    <Component {...pageProps} />
                  </Base>
                </IntlProvider>
              </LaunchDarklyProvider>
            </ApolloProvider>
          </ReduxProvider>
        </ThemeProvider>
      </MaterialProvider>
    </>
  );
};

export default PunctClient;
